import type { AppRole, Principal } from '@mdt/contracts';

/**
 * Authorization predicates (roadmap 2.3). Explicit roles come from live `role_grant` rows (loaded per
 * request, so a revocation applies to the very next request); implicit roles (student, member,
 * supervisor, panelist) are facts the command checks in its own transaction.
 *
 * A grant with `cohortId = null` is department-wide and satisfies a check for any cohort. A cohort-scoped
 * grant satisfies only its own cohort. Nothing here ever derives a role from token claims (ADR-14).
 */
export function hasRole(p: Pick<Principal, 'grants'>, role: AppRole, cohortId?: string | null): boolean {
  return p.grants.some((g) => g.role === role && (g.cohortId === null || (cohortId != null && g.cohortId === cohortId)));
}

/** Department-wide administrator: the only grant that sets `mdt.is_admin` (RLS) and runs identity commands. */
export function isAdmin(p: Pick<Principal, 'grants'>): boolean {
  return p.grants.some((g) => g.role === 'admin' && g.cohortId === null);
}

/** Staff of a cohort: an admin (department or cohort), or a teacher/officer grant for it. */
export function isCohortStaff(p: Pick<Principal, 'grants'>, cohortId: string): boolean {
  return (['admin', 'teacher', 'lab_officer', 'research_officer'] as const).some((r) => hasRole(p, r, cohortId));
}

/** The explicit roles of a principal, deduplicated, for `mdt.roles`. */
export function roleList(p: Pick<Principal, 'grants'>): AppRole[] {
  return [...new Set(p.grants.map((g) => g.role))].sort();
}
